// Color palettes
//
// MD3 Design Principle:
// In Material Design 3, visual hierarchy is achieved through elevation (surface tinting)
// rather than different background colors. Cards, papers, and other surfaces should use
// the same base background color, with elevation levels providing the visual distinction.
import type { ThemeMode } from './ThemeContext';

const colorPalettes = {
  palette1: {
    blueJinns: '#002D72',
    nightBloomingJasmine: '#F9F7EC',
    neonLagoon: '#09CDCD',
    purplePleasures: '#81459E',
    seaGlassTeal: '#A0E5D9',
    ruthlessEmpress: '#573894',
  },
  palette2: {
    eyeball: '#FFFBF8',
    coolFrost: '#E7E6ED',
    insomnia: '#06012C',
    sleeplesBlue: '#BADBED',
    englishDaisy: '#FFCA46',
    purpleVelour: '#581A57',
  },
  palette3: {
    doctor: '#F9F9F9',
    crowBlackBlue: '#112F4B',
    scandinavianSky: '#B6DCAD',
    purpleEmperor: '#6633BB',
    springSun: '#F1F1C6',
    prairie: '#0B9D6A',
  },
  palette4: {
    silicaSand: '#EDE2E0',
    brightWhite: '#F6F2F1',
    panorama: '#327A88',
    panoramaBright: '#428F98',
    mysticRose: '#F8C0BA',
    camelliaRose: '#E94E6D',
    silentTwilight: '#0F084B',
    silentTwilightDim: '#080426',
  },
  palette5: {
    trueWhite: '#FFFFFF',
    trueBlack: '#000000',
    gray: '#EFEFEF',
    heiSeBlack: '#142030',
    siestaTan: '#E9D8C8',
    siestaTanLight: '#FFF4E8',
    picoEggplant: '#732553',
    stellarStrawberry: '#FF5C8D',
    blueWhale: '#1E3442',
    grauZone: '#85A3B2',
  },
} as const;

// Type for palette names
type PaletteName = keyof typeof colorPalettes;

// Type for color names within each palette
type ColorName<P extends PaletteName> = keyof (typeof colorPalettes)[P];

// Get all unique color names across all palettes for intellisense
type AllColorNames =
  | ColorName<'palette1'>
  | ColorName<'palette2'>
  | ColorName<'palette3'>
  | ColorName<'palette4'>
  | ColorName<'palette5'>;

// Define interpolation object for computed colors
interface ColorInterpolation {
  from: AllColorNames;
  degree: number; // 0 to 1, where 0 = from color, 1 = to color
  to: AllColorNames;
}

// Allow color values to be either direct color names or interpolation objects
type ColorValue = AllColorNames | ColorInterpolation;

// Define the semantic color roles used in our theme
interface ThemeColorMapping {
  // Background colors (standard MUI)
  // In MD3, backgroundPaper should be the same as backgroundDefault
  // Visual hierarchy is achieved through elevation/tinting
  backgroundDefault: ColorValue;
  backgroundPaper: ColorValue;

  // Text colors
  textPrimary: ColorValue;
  textSecondary: ColorValue;

  // UI colors
  primary: ColorValue;
  secondary: ColorValue;
  info: ColorValue;
  error: ColorValue;
  warning: ColorValue;
  success: ColorValue;

  // Component-specific colors
  appBarBg: ColorValue;
  appBarFg: ColorValue;
  contextPanelBg: ColorValue;
  contextPanelFg: ColorValue;
  floatingWidgetBg: ColorValue; // Used for omnibox, home button, and mobile context pills
  floatingWidgetFg: ColorValue;
}

// Select which color mapping to use: 'coolBlue', 'warmPurple', 'nature', 'ocean', 'strawberry', or 'strawberryBlend'
export const COLOR_MAPPING = 'strawberryBlend';

// Define preset color mappings
// Notes:
// - the AppBar is "primary" in light mode and "backgroundPaper" in dark mode
const colorMappings: Record<
  string,
  {
    light: ThemeColorMapping;
    dark: ThemeColorMapping;
    palette: PaletteName;
  }
> = {
  // Cool blue/purple theme
  coolBlue: {
    palette: 'palette1',
    light: {
      backgroundDefault: 'nightBloomingJasmine',
      backgroundPaper: 'nightBloomingJasmine',
      textPrimary: 'blueJinns',
      textSecondary: 'ruthlessEmpress',
      primary: 'seaGlassTeal',
      secondary: 'ruthlessEmpress',
      info: 'neonLagoon',
      error: 'purplePleasures',
      warning: 'purplePleasures',
      success: 'neonLagoon',
      appBarBg: 'seaGlassTeal',
      appBarFg: 'blueJinns',
      contextPanelBg: 'nightBloomingJasmine',
      contextPanelFg: 'blueJinns',
      floatingWidgetBg: 'nightBloomingJasmine',
      floatingWidgetFg: 'blueJinns',
    },
    dark: {
      backgroundDefault: 'blueJinns',
      backgroundPaper: 'blueJinns',
      textPrimary: 'nightBloomingJasmine',
      textSecondary: 'seaGlassTeal',
      primary: 'neonLagoon',
      secondary: 'seaGlassTeal',
      info: 'neonLagoon',
      error: 'purplePleasures',
      warning: 'seaGlassTeal',
      success: 'neonLagoon',
      appBarBg: 'blueJinns',
      appBarFg: 'nightBloomingJasmine',
      contextPanelBg: 'blueJinns',
      contextPanelFg: 'nightBloomingJasmine',
      floatingWidgetBg: 'blueJinns',
      floatingWidgetFg: 'nightBloomingJasmine',
    },
  },

  // Warm purple/yellow theme
  warmPurple: {
    palette: 'palette2',
    light: {
      backgroundDefault: 'eyeball',
      backgroundPaper: 'eyeball',
      textPrimary: 'insomnia',
      textSecondary: 'purpleVelour',
      primary: 'purpleVelour',
      secondary: 'insomnia',
      info: 'sleeplesBlue',
      error: 'purpleVelour',
      warning: 'englishDaisy',
      success: 'sleeplesBlue',
      appBarBg: 'purpleVelour',
      appBarFg: 'eyeball',
      contextPanelBg: 'coolFrost',
      contextPanelFg: 'insomnia',
      floatingWidgetBg: 'eyeball',
      floatingWidgetFg: 'insomnia',
    },
    dark: {
      backgroundDefault: 'insomnia',
      backgroundPaper: 'insomnia',
      textPrimary: 'eyeball',
      textSecondary: 'coolFrost',
      primary: 'englishDaisy',
      secondary: 'sleeplesBlue',
      info: 'coolFrost',
      error: 'englishDaisy',
      warning: 'englishDaisy',
      success: 'sleeplesBlue',
      appBarBg: 'insomnia',
      appBarFg: 'eyeball',
      contextPanelBg: 'purpleVelour',
      contextPanelFg: 'coolFrost',
      floatingWidgetBg: 'insomnia',
      floatingWidgetFg: 'eyeball',
    },
  },

  // Nature green/purple theme
  nature: {
    palette: 'palette3',
    light: {
      backgroundDefault: 'doctor',
      backgroundPaper: 'doctor',
      textPrimary: 'crowBlackBlue',
      textSecondary: 'purpleEmperor',
      primary: 'crowBlackBlue',
      secondary: 'purpleEmperor',
      info: 'prairie',
      error: 'purpleEmperor',
      warning: 'springSun',
      success: 'prairie',
      appBarBg: 'crowBlackBlue',
      appBarFg: 'doctor',
      contextPanelBg: 'scandinavianSky',
      contextPanelFg: 'crowBlackBlue',
      floatingWidgetBg: 'doctor',
      floatingWidgetFg: 'crowBlackBlue',
    },
    dark: {
      backgroundDefault: 'crowBlackBlue',
      backgroundPaper: 'crowBlackBlue',
      textPrimary: 'doctor',
      textSecondary: 'scandinavianSky',
      primary: 'scandinavianSky',
      secondary: 'prairie',
      info: 'springSun',
      error: 'scandinavianSky',
      warning: 'springSun',
      success: 'prairie',
      appBarBg: 'crowBlackBlue',
      appBarFg: 'doctor',
      contextPanelBg: 'purpleEmperor',
      contextPanelFg: 'doctor',
      floatingWidgetBg: 'crowBlackBlue',
      floatingWidgetFg: 'doctor',
    },
  },

  // Ocean teal/rose theme
  ocean: {
    palette: 'palette4',
    light: {
      backgroundDefault: 'silicaSand',
      backgroundPaper: 'brightWhite',
      textPrimary: 'silentTwilight',
      textSecondary: 'panorama',
      primary: 'panoramaBright',
      secondary: 'silentTwilight',
      info: 'panorama',
      error: 'camelliaRose',
      warning: 'camelliaRose',
      success: 'panorama',
      appBarBg: 'panoramaBright',
      appBarFg: 'brightWhite',
      contextPanelBg: 'brightWhite',
      contextPanelFg: 'silentTwilight',
      floatingWidgetBg: 'brightWhite',
      floatingWidgetFg: 'silentTwilight',
    },
    dark: {
      backgroundDefault: 'silentTwilightDim',
      backgroundPaper: 'silentTwilight',
      textPrimary: 'brightWhite',
      textSecondary: 'mysticRose',
      primary: 'panorama',
      secondary: 'camelliaRose',
      info: 'mysticRose',
      error: 'camelliaRose',
      warning: 'camelliaRose',
      success: 'panorama',
      appBarBg: 'silentTwilight',
      appBarFg: 'brightWhite',
      contextPanelBg: 'panorama',
      contextPanelFg: 'brightWhite',
      floatingWidgetBg: 'silentTwilight',
      floatingWidgetFg: 'brightWhite',
    },
  },

  // Strawberry theme with tan and eggplant accents
  strawberry: {
    palette: 'palette5',
    light: {
      backgroundDefault: 'siestaTan',
      backgroundPaper: 'siestaTanLight',
      textPrimary: 'heiSeBlack',
      textSecondary: 'blueWhale',
      primary: 'grauZone',
      secondary: 'picoEggplant',
      info: 'grauZone',
      error: 'picoEggplant',
      warning: 'stellarStrawberry',
      success: 'grauZone',
      appBarBg: 'picoEggplant',
      appBarFg: 'siestaTanLight',
      contextPanelBg: 'siestaTanLight',
      contextPanelFg: 'heiSeBlack',
      floatingWidgetBg: 'siestaTanLight',
      floatingWidgetFg: 'heiSeBlack',
    },
    dark: {
      backgroundDefault: 'heiSeBlack',
      backgroundPaper: 'heiSeBlack',
      textPrimary: 'siestaTan',
      textSecondary: 'grauZone',
      primary: 'stellarStrawberry',
      secondary: 'grauZone',
      info: 'grauZone',
      error: 'stellarStrawberry',
      warning: 'stellarStrawberry',
      success: 'grauZone',
      appBarBg: 'heiSeBlack',
      appBarFg: 'siestaTan',
      contextPanelBg: 'blueWhale',
      contextPanelFg: 'siestaTan',
      floatingWidgetBg: 'heiSeBlack',
      floatingWidgetFg: 'siestaTan',
    },
  },

  strawberryBlend: {
    palette: 'palette5',
    light: {
      backgroundDefault: 'trueWhite', // { from: 'siestaTan', degree: 1.0, to: 'trueWhite' },
      backgroundPaper: 'trueWhite', // { from: 'siestaTan', degree: 1.0, to: 'trueWhite' },
      textPrimary: 'heiSeBlack',
      textSecondary: { from: 'blueWhale', degree: 0.5, to: 'grauZone' },
      primary: { from: 'picoEggplant', degree: 0.0, to: 'trueWhite' },
      secondary: 'blueWhale',
      info: 'picoEggplant',
      error: 'stellarStrawberry',
      warning: { from: 'stellarStrawberry', degree: 0.5, to: 'picoEggplant' },
      success: 'grauZone',
      appBarBg: 'trueWhite',
      appBarFg: 'picoEggplant',
      contextPanelBg: 'trueWhite',
      contextPanelFg: 'heiSeBlack',
      floatingWidgetBg: 'trueWhite',
      floatingWidgetFg: 'heiSeBlack',
    },
    dark: {
      backgroundDefault: 'trueBlack',
      backgroundPaper: { from: 'heiSeBlack', degree: 0.5, to: 'trueBlack' },
      textPrimary: 'siestaTan',
      textSecondary: 'grauZone',
      primary: 'stellarStrawberry',
      secondary: 'picoEggplant', //{ from: 'grauZone', degree: 0.4, to: 'stellarStrawberry' },
      info: 'grauZone',
      error: 'stellarStrawberry',
      warning: 'stellarStrawberry',
      success: 'grauZone',
      appBarBg: { from: 'heiSeBlack', degree: 0.5, to: 'trueBlack' },
      appBarFg: 'siestaTan',
      contextPanelBg: { from: 'heiSeBlack', degree: 0.5, to: 'trueBlack' },
      contextPanelFg: 'siestaTan',
      floatingWidgetBg: { from: 'heiSeBlack', degree: 0.3, to: 'trueBlack' },
      floatingWidgetFg: 'siestaTan',
    },
  },
};

// Helper function to check if a value is a ColorInterpolation object
function isColorInterpolation(value: ColorValue): value is ColorInterpolation {
  return typeof value === 'object' && 'from' in value && 'degree' in value && 'to' in value;
}

// Helper function to resolve a single color value (handles both direct colors and interpolations)
function resolveColorValue(
  value: ColorValue,
  palette: (typeof colorPalettes)[PaletteName]
): string {
  if (isColorInterpolation(value)) {
    const fromColor = palette[value.from as keyof typeof palette] as string;
    const toColor = palette[value.to as keyof typeof palette] as string;
    return interpolateColors(fromColor, toColor, value.degree);
  } else {
    return palette[value as keyof typeof palette] as string;
  }
}

// Helper function to resolve color values from mapping
export function resolveColors(
  mappingName: string,
  mode: ThemeMode // Only accepts resolved 'light' | 'dark', not 'system'
): Record<keyof ThemeColorMapping, string> {
  const mapping = colorMappings[mappingName];
  if (!mapping) {
    throw new Error(`Color mapping "${mappingName}" not found`);
  }

  const palette = colorPalettes[mapping.palette];
  const colorMap = mapping[mode];

  const resolved: Partial<Record<keyof ThemeColorMapping, string>> = {};

  for (const [role, colorValue] of Object.entries(colorMap)) {
    resolved[role as keyof ThemeColorMapping] = resolveColorValue(
      colorValue as ColorValue,
      palette
    );
  }

  return resolved as Record<keyof ThemeColorMapping, string>;
}

// Create a new MD3-compliant color mapping
// This ensures backgroundPaper === backgroundDefault
function createMD3ColorMapping(
  palette: PaletteName,
  light: Omit<ThemeColorMapping, 'backgroundPaper'>,
  dark: Omit<ThemeColorMapping, 'backgroundPaper'>
) {
  return {
    palette,
    light: {
      ...light,
      backgroundPaper: light.backgroundDefault,
    },
    dark: {
      ...dark,
      backgroundPaper: dark.backgroundDefault,
    },
  };
}

// Utility function to interpolate between two colors
// Takes two color hex values and returns a color that is 'amount' of the way from color1 to color2
// amount should be between 0 and 1, where 0 returns color1 and 1 returns color2
function interpolateColors(color1: string, color2: string, amount: number): string {
  // Ensure amount is between 0 and 1
  amount = Math.max(0, Math.min(1, amount));

  // Convert hex to RGB
  const hexToRgb = (hex: string): [number, number, number] => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    if (!result) {
      throw new Error(`Invalid hex color: ${hex}`);
    }
    return [parseInt(result[1], 16), parseInt(result[2], 16), parseInt(result[3], 16)];
  };

  // Convert RGB back to hex
  const rgbToHex = (r: number, g: number, b: number): string => {
    const toHex = (n: number) => {
      const hex = Math.round(n).toString(16);
      return hex.length === 1 ? '0' + hex : hex;
    };
    return '#' + toHex(r) + toHex(g) + toHex(b);
  };

  const [r1, g1, b1] = hexToRgb(color1);
  const [r2, g2, b2] = hexToRgb(color2);

  // Interpolate each channel
  const r = r1 + (r2 - r1) * amount;
  const g = g1 + (g2 - g1) * amount;
  const b = b1 + (b2 - b1) * amount;

  return rgbToHex(r, g, b);
}

// Example usage:
// To create a color mapping with interpolated colors, you would need to:
// 1. Create the interpolated colors as constants
// 2. Add them to the palette
// 3. Reference them in the color mapping
//
// For example:
// const myInterpolatedColor = interpolate('palette2', 'eyeball', 0.25, 'englishDaisy');
// Then add this color to your palette with a descriptive name
